import { NonUserRole, UserRole } from '@fieldday/fielddayportal-model';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, createContext, useContext, useState } from 'react';
import { NPOWithActivities } from '../models/Npo';
import { Employer, OrgUnit } from '../models/OrgUnit';
import { OrgType } from '../util/useAPI';

export type ContextProps = {
  children: ReactNode
  npo?: NPOWithActivities
  orgUnit?: OrgUnit
  role?: UserRole | NonUserRole
  setOrgUnit?: (orgUnit: OrgUnit) => void
  employer?: Employer
  setEmployer?: (employer: Employer) => void
}

export type roleChecker = (roleToCheck?: UserRole) => boolean

interface DataForContext {
  npo?: NPOWithActivities
  orgUnit?: OrgUnit
  orgType: OrgType
  orgId: string
  handle?: string
  orgName?: string
  role: UserRole | NonUserRole | undefined
  canAdmin: roleChecker
  canCoordinate: roleChecker
  canManageDonations: roleChecker
  isMember: roleChecker
  loadedAt: Dayjs // used for triggering refreshes across components
  updateLoadedAt: () => void
  flags: string[]
  setOrgUnit?: (orgUnit: OrgUnit) => void
  employer?: Employer
  setEmployer?: (employer: Employer) => void
}

interface DataForNpoContext extends DataForContext {
  npo: NPOWithActivities
}

export interface DataForOrgUnitContext extends DataForContext {
  orgUnit: OrgUnit
  setOrgUnit: (orgUnit: OrgUnit) => void
  employer?: Employer
  setEmployer?: (employer: Employer) => void
}

const OrgContext = createContext<DataForContext | undefined>(undefined)

export function RoleStatements(role?: UserRole | NonUserRole): {
  canAdmin: roleChecker,
  canCoordinate: roleChecker,
  isMember: roleChecker,
  canManageDonations: roleChecker
} {
  const canAdmin = (roleToCheck: UserRole | NonUserRole| undefined = role) => roleToCheck === UserRole.Owner
  const canCoordinate = (roleToCheck: UserRole | NonUserRole | undefined = role) => canAdmin() || roleToCheck === UserRole.Coordinator
  const canManageDonations = (roleToCheck: UserRole | NonUserRole | undefined = role) => canAdmin() || (roleToCheck === UserRole.GivingManager)
  const isMember = (roleToCheck: UserRole | NonUserRole | undefined = role) =>
    !!roleToCheck && (Object.values(UserRole) as Array<string>).includes(roleToCheck)

  return { canAdmin, canCoordinate, isMember, canManageDonations }
}

// to use OrgProvider, include either npo or orgUnit, then use the related context hook
export function OrgProvider({ npo, orgUnit, role, setOrgUnit, children, employer, setEmployer }: ContextProps) {
  if (!npo && !orgUnit) throw new Error("Either npo or orgUnit required for OrgProvider")
  const [loadedAt, setLoadedAt] = useState(dayjs())
  // npo only requires logo and image if not owned by a team
  if (npo) npo.hasCompleteProfile = !!npo.orgUnitId || (!!npo.logo && !!npo.bannerImage)
  if (orgUnit) orgUnit.hasCompleteProfile = !!orgUnit.logo

  const { canAdmin, canCoordinate, isMember, canManageDonations } = RoleStatements(role)
  const updateLoadedAt = () => {
    setLoadedAt(dayjs())
  }

  const orgType: OrgType = npo ? "nonprofit" : "orgunit"
  // we just validated it's one or the other!
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const orgId: string = npo ? npo.id : orgUnit!.id
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const handle = npo ? npo.handle : orgUnit!.handle
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const orgName = npo ? npo.name : orgUnit!.name

  // Add flags for the relevant org unit
  const flags = npo ? npo.flags : orgUnit?.flags

  const context: DataForContext = {
    npo,
    orgUnit,
    orgType,
    orgId,
    handle,
    orgName,
    role,
    canAdmin,
    canCoordinate,
    canManageDonations,
    isMember,
    loadedAt,
    updateLoadedAt,
    flags: flags ?? [],
    setOrgUnit,
    employer,
    setEmployer
  }

  // console logs for easy access to IDs
  if (npo) console.log("NPO", { nonProfitOrgId: npo.id, name: npo.name, handle: npo.handle })
  if (orgUnit) console.log("OU", { orgUnitId: orgUnit.id, name: orgUnit.name, handle: orgUnit.handle })

  return (
    <OrgContext.Provider value={context}>
      {children}
    </OrgContext.Provider>
  )
}

// might be NPO, might be OrgUnit - npo/orgUnit properties are ?
export const useCommonOrgContext = () => {
  const context = useContext(OrgContext)
  if (!context?.npo && !context?.orgUnit) {
    throw new Error('useCommonOrgContext must be used within an OrgProvider that has either npo or orgUnit')
  }
  return context
}

// only for NPO - npo property is defined
export const useNonprofitContext = () => {
  const context = useContext(OrgContext)
  // NOTE: we used to validate npo, but this may be used in places where an NPO is not relevant
  // e.g. SA images context for independent FTs
  return context as DataForNpoContext
}

// only for OrgUnit - orgUnit property is defined
export const useOrgUnitContext = () => {
  const context = useContext(OrgContext)
  if (!context?.orgUnit) throw new Error('useOrgUnitContext must be used within an OrgProvider that has an orgUnit')
  if (!context?.setOrgUnit) throw new Error('useOrgUnitContext must be used with an OrgProvider that defines setOrgUnit')
  return context as DataForOrgUnitContext
}
